import React, { useState } from 'react';
import { Checkbox, Row, Divider, Tooltip, Col, Button } from 'antd';

import { ReactComponent as ExportIcon } from '../../../images/icon-export.svg';
import { ReactComponent as DeleteOutlined } from '../../../images/delete-outline.svg';
import { GlossaryNode, GlossaryTerm } from '../../../types.generated';
import { Notification } from '../../shared/Notification';
import { ComponentSpinner } from '../../shared/ComponentSpinner';

import { buildGlossaryExportInput, resolveGlossaryBulkActionStates } from '../../glossary/utils';
import { ChildGlossaryTermFragment } from '../../../graphql/glossaryNode.generated';
import { useExportEntityMutation } from '../../../graphql/mutations.generated';
import { GlossaryNodeFragment } from '../../../graphql/fragments.generated';
import { useTranslation } from '../../../i18n/useTranslation';

interface Props {
    nodes: (GlossaryNode | GlossaryNodeFragment)[];
    terms: (GlossaryTerm | ChildGlossaryTermFragment)[];
    selectedItems: string[];
    isDeleting?: boolean;
    canExport: boolean;
    canDeleteTerm: boolean;
    canDeleteNode: boolean;
    isAllSelected: () => boolean;
    handleBulkDelete?: () => void;
    isIndeterminate: () => boolean;
    handleSelectAll: (checked: boolean) => void;
    setSelectedItems: (items: string[]) => void;
}

function GlossaryActions({
    nodes,
    terms,
    canExport,
    canDeleteTerm,
    canDeleteNode,
    isDeleting,
    selectedItems,
    isAllSelected,
    setSelectedItems,
    handleSelectAll,
    isIndeterminate,
    handleBulkDelete,
}: Props) {
    const canDelete = canDeleteTerm || canDeleteNode;
    const { hasSelectedDeletableItems, hasSelectedExportableItems } = resolveGlossaryBulkActionStates({
        terms,
        nodes,
        selectedItems,
        canDeleteTerm,
        canDeleteNode,
        canExport,
    });
    const { t } = useTranslation();
    const [isCsvExporting, setIsCsvExporting] = useState<boolean>(false);
    const [exportEntity, setExportEntity]: any = useState(null);
    const [exportEntityMutation] = useExportEntityMutation();

    const isLoading = isCsvExporting || isDeleting;

    /**
     * Handles the export of the selected glossary entities to a CSV file.
     * Retries asynchronously on a 503 and clears the selection on success.
     */
    const exportCsv = () => {
        setIsCsvExporting(true);
        setExportEntity(null);

        const input = buildGlossaryExportInput(nodes, terms, selectedItems);

        exportEntityMutation({
            variables: {
                input,
            },
        })
            .then((res = {}) => {
                const { data } = res;
                setExportEntity(data?.exportEntity);
                setSelectedItems([]);
            })
            .catch((e) => {
                if (e.networkError.statusCode === 503) {
                    const updatedInput = {
                        ...input,
                        async: true,
                    };
                    // Retry with a different input for 504 errors
                    exportEntityMutation({
                        variables: {
                            input: updatedInput,
                        },
                    }).then((retryRes) => {
                        const { data } = retryRes;
                        setExportEntity(data?.exportEntity);
                        setSelectedItems([]);
                    });
                }
            })
            .finally(() => setIsCsvExporting(false));
    };

    return (
        <>
            {exportEntity && <Notification exportEntity={exportEntity} />}

            <Row className="px-3 py-2" align="middle" gutter={[8, 8]}>
                <Col flex="none">
                    <Button
                        type="text"
                        onClick={exportCsv}
                        className="f-btn-flat"
                        disabled={!hasSelectedExportableItems}
                    >
                        <ExportIcon aria-hidden="true" />
                        <span>{t('Export')}</span>
                    </Button>
                </Col>
                <Col flex="none">
                    <Button
                        type="text"
                        className="f-btn-flat"
                        onClick={handleBulkDelete}
                        disabled={!hasSelectedDeletableItems}
                    >
                        <DeleteOutlined aria-hidden="true" />
                        <span>{t('Delete')}</span>
                    </Button>
                </Col>
            </Row>
            <Divider className="ma-0" />
            <Row className="px-4 py-3" align="middle" gutter={[8, 8]}>
                <Col flex="auto">
                    <Tooltip
                        title={!canDelete && !canExport ? t('UNAUTHORISED_TOOLTIP') : undefined}
                        overlayClassName="f-tooltip-white"
                    >
                        <Checkbox
                            checked={isAllSelected()}
                            indeterminate={isIndeterminate()}
                            disabled={isLoading || (!canDelete && !canExport)}
                            onChange={(e) => handleSelectAll(e.target.checked)}
                        >
                            {t('Select All')}
                        </Checkbox>
                    </Tooltip>
                </Col>
            </Row>
            {isLoading && <ComponentSpinner />}
            <Divider className="ma-0" />
        </>
    );
}

export default GlossaryActions;
